import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import * as yup from "yup";
import AppInput from "../../components/Core/AppInput/AppInput";
import AppText from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import Checkbox from "../../components/Core/Checkbox/Checkbox";
import { DropdownController } from "../../components/Core/Dropdown/Dropdown";
import Flex from "../../components/Layout/Flex";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import { EmploymentAndSalaryController } from "../../components/Shared/EmploymentAndSalary/EmploymentAndSalary";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { BANKS } from "../../configs/banks.configs";
import { CreateConsultantBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import { FEEDBACK_MESSAGE_TYPE } from "../../configs/feedbackMessages";
import { Bank, CreateConsultantRequest, Employment, Salary } from "../../models/Consultant";
import { KeyValue } from "../../models/KeyValue";
import { Pool } from "../../models/Organisation";
import { useCreateConsultant } from "../../networking/consultant.hooks";
import phoneNumberSchema from "../../schemas/phoneNumberSchema";
import socialSecurityNumberSchema from "../../schemas/socialSecurityNumberSchema";
import { ConfirmModalProps } from "../../stores/confirmModal.store";
import { formatRoute } from "../../utils/route.utils";
import { useCustomFieldDefinitions, useOrganisation } from "../../networking/organisation.hooks";
import {
	CustomFieldFormValues,
	CustomFieldsSection,
	buildCustomFieldsPayload,
	customFieldDefaults,
	customFieldsSchema,
} from "../../components/Shared/CustomFields";
import { useSubsidiaries } from "../../networking/subsidiary.hooks";
import SubsidiarySelect, {
	useAvailablePools,
} from "../../components/Shared/SubsidiarySelect/SubsidiarySelect";
import { SalaryFormController } from "../../components/Shared/SalaryForm/SalaryForm";
import { Box, VStack } from "@chakra-ui/react";
import { toast } from "../../utils/toast";
import { useConfirmModalStore } from "../../stores/confirmModal.store";

type ConsultantsEmpAndSalaryInputs = {
	active?: boolean;
	firstName: string;
	lastName: string;
	email: string;
	profile: {
		employment: Omit<Employment, "salary"> & { salary: Salary };
		bank: Bank;
		socialSecurityNumber: string;
		phoneNumber: string;
		pool: string;
		subsidiaryId?: string;
	};
	disablePublishMatchingJobs?: boolean;
	/** Form-level group; mapped into `profile.customFields` on submit. */
	customFields?: CustomFieldFormValues;
};

const ConsultantsInputsValidationSchema = yup.object().shape({
	email: yup.string().email("EMAIL_NOT_VALID").required("FIELD_IS_REQUIRED"),
	firstName: yup.string().required("FIELD_IS_REQUIRED"),
	lastName: yup.string().required("FIELD_IS_REQUIRED"),
	profile: yup.object().shape({
		phoneNumber: phoneNumberSchema.required("FIELD_IS_REQUIRED"),
		socialSecurityNumber: socialSecurityNumberSchema.required("FIELD_IS_REQUIRED"),
		bank: yup.object().shape({
			// accountNumber: yup.string().required("FIELD_IS_REQUIRED"),
			// name: yup.string().required("FIELD_IS_REQUIRED"),
		}),
		employment: yup.object().shape({
			firstStarted: yup.date().required("FIELD_IS_REQUIRED").typeError(""),
			// salary: yup
			// 	.array()
			// 	.of(
			// 		yup.object().shape({
			// 			amount: yup.number().required("FIELD_IS_REQUIRED").typeError(""),
			// 			type: yup.string().required("EMPLOYMENT_TYPE_REQUIRED"),
			// 			holidayPay: yup.number().required("FIELD_IS_REQUIRED"),
			// 		})
			// 	)
			// 	.required("SALARY_DETAILS_REQUIRED"),
		}),
	}),
});

const CreateConsultantsPage: React.FC = () => {
	const modalStore = useConfirmModalStore();
	const { mutateAsync: createConsultant, isLoading: isCreating } = useCreateConsultant();

	const { data: organisation } = useOrganisation();

	const { t } = useTranslation();
	const navigate = useNavigate();

	// Org-defined "Övriga uppgifter" fields (required-ness is form-only).
	const customFieldDefinitions = useCustomFieldDefinitions("consultant");
	const validationSchema = useMemo(
		() =>
			ConsultantsInputsValidationSchema.shape({
				customFields: customFieldsSchema(customFieldDefinitions),
			}),
		[customFieldDefinitions]
	);

	const { control, handleSubmit, register, formState, setValue } =
		useForm<ConsultantsEmpAndSalaryInputs>({
			mode: "onBlur",
			resolver: yupResolver(validationSchema),
			defaultValues: { customFields: {} },
		});

	// Seed the custom fields with their definition defaults once the org has
	// loaded — once only, so a background refetch can't clobber edits.
	const customFieldsSeeded = useRef(false);
	useEffect(() => {
		if (customFieldsSeeded.current || customFieldDefinitions.length === 0) return;
		customFieldsSeeded.current = true;
		setValue("customFields", customFieldDefaults(customFieldDefinitions));
	}, [customFieldDefinitions, setValue]);

	const { subsidiaries } = useSubsidiaries();

	const availablePools = useAvailablePools(
		control,
		"profile.subsidiaryId",
		subsidiaries,
		organisation?.pools
	);

	const onSubmit = handleSubmit(async (e) => {
		const createConsultantRequest: CreateConsultantRequest = {
			active: e.active,
			firstName: e.firstName,
			lastName: e.lastName,
			email: e.email,
			profile: {
				employment: {
					...e.profile.employment,
					salary: [e.profile.employment.salary],
				},
				bank: e.profile.bank,
				socialSecurityNumber: e.profile.socialSecurityNumber,
				phoneNumber: e.profile.phoneNumber,
				pool: e.profile.pool,
				subsidiaryId: e.profile.subsidiaryId || undefined,
				// Always the full map — undefined when the org has no consultant fields.
				customFields: buildCustomFieldsPayload(e.customFields, customFieldDefinitions),
			},
			disablePublishMatchingJobs: e.disablePublishMatchingJobs,
		};

		// The hook invalidates the consultant lists; mutateAsync rejects on
		// failure, so the confirm modal is skipped.
		const result = await createConsultant(createConsultantRequest).catch(() => undefined);

		if (result) {
			const consultantConfirmModal: ConfirmModalProps = {
				title: "CONSULTANT_HAS_CREATED",
				text: result.active ? "ACTIVE_CONSULTANT_CREATED_INFO" : "INACTIVE_CONSULTANT_CREATED_INFO",
				acceptButtonText: "OPEN_CONSULTANT_PROFILE",
				rejectButtonText: "NOT_NOW",
				onReject: () => navigateToConsultantList(),
				onAccept: () => navigateToConsultantProfile(result.id),
			};
			modalStore.newModal(consultantConfirmModal);
		} else toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
	});

	const navigateToConsultantList = () => {
		navigate(routes.CONSULTANTS);
	};

	const navigateToConsultantProfile = (consultantId?: string) => {
		const routePath = `${formatRoute(routes.CONSULTANTS_DETAILS, [consultantId])}/${
			routes.CONSULTANTS_PROFILE
		}`;
		navigate(routePath);
	};

	useEffect(() => {
		if (organisation) {
			const defaultPool = organisation?.pools?.find((p) => p.isDefault);

			if (defaultPool) {
				setValue("profile.pool", defaultPool.key);
			}
		}
	}, [organisation]);

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={CreateConsultantBreadcrumbs} />
			<Flex maxWidth="700px" margin="2rem auto 4rem auto" direction="column">
				{!isCreating ? (
					<form onSubmit={onSubmit}>
						<VStack spacing="5" alignItems={"flex-start"} w="full">
							<AppText color="grayText" size="xm" weight="bold">
								{t("BASIC_DETAILS")}
							</AppText>

							<AppInput
								{...register("firstName")}
								icon="name"
								label="FIRST_NAME"
								placeholder="FIRST_NAME"
								errors={formState.errors?.firstName?.message}
								containerMargin="0"
								margin="0"
								mandatory
							/>
							<AppInput
								{...register("lastName")}
								icon="name"
								label="LAST_NAME"
								placeholder="LAST_NAME"
								errors={formState.errors?.lastName?.message}
								containerMargin="0"
								margin="0"
								mandatory
							/>

							<AppInput
								{...register("email")}
								icon="email"
								label="EMAIL"
								placeholder="EMAIL"
								errors={formState.errors?.email?.message}
								containerMargin="0"
								margin="0"
								mandatory
							/>
							<AppInput
								{...register("profile.phoneNumber")}
								icon="phone"
								label="PHONE"
								placeholder={t("PHONE_PLACEHOLDER")}
								errors={formState.errors?.profile?.phoneNumber?.message}
								containerMargin="0"
								mandatory
							/>

							<AppInput
								{...register("profile.socialSecurityNumber")}
								label="PERSONAL_NUMBER"
								placeholder="ÅÅÅÅMMDD-XXXX"
								errors={formState.errors?.profile?.socialSecurityNumber?.message}
								containerMargin="0"
								mandatory
							/>

							{subsidiaries.length > 0 && (
								<SubsidiarySelect
									control={control}
									name="profile.subsidiaryId"
									subsidiaries={subsidiaries}
									margin="0"
								/>
							)}

							{availablePools.length > 0 && (
								<DropdownController<Pool>
									control={control}
									label="POOL"
									name="profile.pool"
									options={availablePools}
									getOptionLabel={(option) => option.name}
									getValue={(o) => o.key as string}
									placeholder="SELECT_POOL"
									margin="0"
								/>
							)}

							{/* Consultant fields placed under Grunduppgifter — hidden when there are none. */}
							<Box w="full">
								<CustomFieldsSection
									entity="consultant"
									placement="grunduppgifter"
									variant="inline"
									className="mt-0"
									definitions={customFieldDefinitions}
									control={control}
									register={register}
									errors={formState.errors}
								/>
							</Box>

							<AppText margin="1rem 0 0 0" color="grayText" size="xm" weight="bold">
								{t("EMPLOYMENT")}
							</AppText>

							<EmploymentAndSalaryController
								control={control}
								errors={formState.errors?.profile?.employment}
								name="profile.employment"
								isCreate
							/>

							{/* Consultant fields placed under Anställning — hidden when there are none. */}
							<Box w="full">
								<CustomFieldsSection
									entity="consultant"
									placement="anstallning"
									variant="inline"
									className="mt-0"
									definitions={customFieldDefinitions}
									control={control}
									register={register}
									errors={formState.errors}
								/>
							</Box>

							<AppText margin="1rem 0 0 0" color="grayText" size="xm" weight="bold">
								{t("SALARY")}
							</AppText>

							<Box w="full">
								<SalaryFormController
									control={control}
									name="profile.employment.salary"
									// errors={formState.errors?.profile?.employment?.salary}
								/>
							</Box>

							<DropdownController<KeyValue>
								control={control}
								name="profile.bank.name"
								options={BANKS}
								label="BANK"
								getValue={(option) => option.key as string}
								getOptionLabel={(option) => option.value}
								errors={formState.errors?.profile?.bank?.name?.message}
								margin="0"
							/>

							<Flex>
								<AppInput
									{...register("profile.bank.clearingNumber")}
									label="CLEARING_NO"
									containerWidth="200px"
									containerMargin="0 0.5rem 0 0"
								/>
								<AppInput
									{...register("profile.bank.accountNumber")}
									label="BANK_ACCOUNT"
									errors={formState.errors?.profile?.bank?.accountNumber?.message}
									containerMargin="0"
								/>
							</Flex>

							<AppText margin="1rem 0 0 0" color="grayText" size="xm" weight="bold">
								{t("ACTIVATION")}
							</AppText>

							<Checkbox
								{...register("active")}
								label={t("ACTIVATE")}
								description="consultant.activateDesc"
								margin="0"
							/>

							<Checkbox
								{...register("disablePublishMatchingJobs")}
								label="consultant.doNotPublish"
								description="consultant.doNotPublishDesc"
								margin="0"
							/>

							{/* Consultant fields placed under "Övriga uppgifter" — hidden when there are none. */}
							<Box w="full" mt={4}>
								<CustomFieldsSection
									entity="consultant"
									placement="ovrigt"
									definitions={customFieldDefinitions}
									control={control}
									register={register}
									errors={formState.errors}
								/>
							</Box>
						</VStack>

						<Flex justifyContent="center" margin="2.5rem 0 0 0">
							<Button width="9.75rem">{t("CREATE_CONSULTANT")}</Button>
						</Flex>
					</form>
				) : (
					<LoadingIndicator />
				)}
			</Flex>
		</div>
	);
};

export default CreateConsultantsPage;
